import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaCheckCircle,
  FaClipboardCheck,
  FaHandsHelping,
  FaShieldAlt,
  FaUserClock,
  FaWheelchair,
} from 'react-icons/fa';

export default function HomeHeroSection() {
  return (
    <section className="relative overflow-hidden bg-[#042f34] py-24 text-white">
      {/* Layered gradients and a subtle grid texture create depth without extra assets. */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_18%,rgba(45,212,191,0.3),transparent_35%),radial-gradient(circle_at_80%_75%,rgba(34,211,238,0.26),transparent_40%),linear-gradient(125deg,#042f34_10%,#0f766e_48%,#155e75_100%)]" />
      <div className="pointer-events-none absolute inset-0 opacity-20 [background:linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:46px_46px]" />

      <div className="mero-wrapper relative grid items-center gap-10 xl:grid-cols-[1.08fr_0.92fr]">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-teal-100 backdrop-blur">
            <FaCheckCircle className="text-emerald-300" />
            Pennsylvania Home Care Agency
          </span>

          <h1 className="mt-6 max-w-3xl text-4xl font-black leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl">
            In-home support for eligible participants and families
          </h1>

          <p className="mt-6 max-w-2xl text-lg leading-8 text-teal-50/95">
            Golden Hands Home Care helps eligible seniors and adults with
            disabilities stay safe, comfortable, and independent at home
            through personalized plans and trusted day-to-day support.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-teal-800 shadow-lg shadow-black/20 transition hover:-translate-y-0.5 hover:bg-teal-50"
            >
              Explore Services
              <FaArrowRight />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border border-white/60 bg-white/5 px-7 py-3.5 text-sm font-bold text-white transition hover:bg-white/15"
            >
              <FaClipboardCheck className="text-cyan-100" />
              Check Eligibility
            </Link>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-teal-50">
            <span className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 backdrop-blur">
              <FaUserClock className="text-amber-200" /> Fast onboarding guidance
            </span>
            <span className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 backdrop-blur">
              <FaShieldAlt className="text-sky-100" /> Screened caregivers
            </span>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -left-10 top-6 h-48 w-48 rounded-full bg-cyan-300/30 blur-3xl" />
          <div className="absolute -bottom-8 right-0 h-56 w-56 rounded-full bg-teal-300/25 blur-3xl" />

          {/* This trust panel combines eligibility, outcomes, and care philosophy in one glance. */}
          <div className="relative rounded-3xl border border-white/25 bg-white/10 p-6 shadow-2xl shadow-black/20 backdrop-blur-md">
            <article className="rounded-2xl border border-white/30 bg-white/15 p-5">
              <h2 className="inline-flex items-center gap-2 text-lg font-bold">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
                  <FaWheelchair className="text-teal-100" />
                </span>
                Who We Support
              </h2>
              <p className="mt-3 text-sm leading-7 text-teal-50">
                Older adults, waiver participants, and adults with chronic
                conditions who qualify for in-home care.
              </p>
            </article>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <article className="rounded-xl border border-white/25 bg-white/10 p-4 text-center">
                <p className="text-2xl font-extrabold">24/7</p>
                <p className="mt-1 text-xs text-teal-100">Family Support</p>
              </article>
              <article className="rounded-xl border border-white/25 bg-white/10 p-4 text-center">
                <p className="text-2xl font-extrabold">1:1</p>
                <p className="mt-1 text-xs text-teal-100">Care Planning</p>
              </article>
            </div>

            <article className="mt-4 rounded-2xl border border-white/30 bg-gradient-to-r from-white/20 to-white/10 p-5">
              <h3 className="inline-flex items-center gap-2 text-base font-bold">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/20">
                  <FaHandsHelping className="text-teal-100" />
                </span>
                Care You Can Trust
              </h3>
              <p className="mt-2 text-sm leading-7 text-teal-50">
                Culturally responsive communication and flexible care plans
                designed around participant goals.
              </p>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}
